/**
 * Man Overboard (MOB) control for the Chart Plotter.
 * One tap marks the current GPS position, sounds an alarm, and shows a live
 * panel with distance/bearing back to the person. Position survives page
 * reloads via localStorage so an accidental refresh doesn't lose the mark.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import styled, { keyframes } from 'styled-components';

const MOB_STORAGE_KEY = 'boatwise_mob';

// ---- Geo math --------------------------------------------------------------
export function distanceNm(lat1, lon1, lat2, lon2) {
  const R = 3440.065; // earth radius in nautical miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function bearingDeg(lat1, lon1, lat2, lon2) {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// ---- Alarm (WebAudio — no asset file needed) --------------------------------
function playMobAlarm() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    let t = ctx.currentTime;
    for (let i = 0; i < 6; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = i % 2 === 0 ? 880 : 660;
      gain.gain.setValueAtTime(0.25, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
      t += 0.45;
    }
    setTimeout(() => ctx.close(), 4000);
  } catch (e) {
    // Audio blocked (no user gesture / unsupported) — marker and panel still work
  }
  if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 600]);
}

// ---- Hook -------------------------------------------------------------------
export function useMob() {
  const [mob, setMob] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(MOB_STORAGE_KEY));
      if (stored && Number.isFinite(stored.latitude) && Number.isFinite(stored.longitude)) return stored;
    } catch (e) { /* corrupt storage — start fresh */ }
    return null;
  });

  const activateMob = (latitude, longitude) => {
    const entry = { latitude, longitude, markedAt: new Date().toISOString() };
    localStorage.setItem(MOB_STORAGE_KEY, JSON.stringify(entry));
    setMob(entry);
    playMobAlarm();
  };

  const clearMob = () => {
    localStorage.removeItem(MOB_STORAGE_KEY);
    setMob(null);
  };

  return { mob, activateMob, clearMob };
}

// ---- Map marker --------------------------------------------------------------
const mobIcon = L.divIcon({
  className: 'mob-marker',
  html: `
    <div style="position: relative; width: 36px; height: 36px;">
      <div style="position:absolute; inset:0; border-radius:50%; background:rgba(220,38,38,0.4); animation: mobPulse 1.2s ease-out infinite;"></div>
      <div style="position:absolute; inset:6px; border-radius:50%; background:#dc2626; border:3px solid white; display:flex; align-items:center; justify-content:center; color:white; font-weight:800; font-size:11px; box-shadow:0 2px 6px rgba(0,0,0,0.4);">MOB</div>
    </div>
    <style>@keyframes mobPulse { 0% { transform: scale(0.6); opacity: 1; } 100% { transform: scale(1.8); opacity: 0; } }</style>
  `,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

export function MobMarker({ mob }) {
  if (!mob) return null;
  return (
    <>
      <Marker position={[mob.latitude, mob.longitude]} icon={mobIcon} zIndexOffset={2000}>
        <Popup>
          <strong>MAN OVERBOARD</strong>
          <br />
          Marked: {new Date(mob.markedAt).toLocaleTimeString()}
          <br />
          {mob.latitude.toFixed(6)}, {mob.longitude.toFixed(6)}
        </Popup>
      </Marker>
      <Circle
        center={[mob.latitude, mob.longitude]}
        radius={50}
        pathOptions={{ color: '#dc2626', fillColor: '#dc2626', fillOpacity: 0.15 }}
      />
    </>
  );
}

// ---- Live panel ---------------------------------------------------------------
const flash = keyframes`
  0%, 100% { background: #dc2626; }
  50% { background: #991b1b; }
`;

const Panel = styled.div`
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: #dc2626;
  animation: ${flash} 1.5s infinite;
  color: white;
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  box-shadow: 0 8px 24px rgba(220, 38, 38, 0.5);
  display: flex;
  align-items: center;
  gap: 1.25rem;
  font-weight: 600;
  flex-wrap: wrap;
  justify-content: center;
`;

const Big = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

const ClearBtn = styled.button`
  background: white;
  color: #dc2626;
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #fee2e2; }
`;

function formatElapsed(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

export function MobPanel({ mob, myLocation, onClear }) {
  const [, forceTick] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!mob) return undefined;
    tickRef.current = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [mob]);

  const stats = useMemo(() => {
    if (!mob || !myLocation) return null;
    return {
      dist: distanceNm(myLocation.latitude, myLocation.longitude, mob.latitude, mob.longitude),
      brg: bearingDeg(myLocation.latitude, myLocation.longitude, mob.latitude, mob.longitude),
    };
  }, [mob, myLocation]);

  if (!mob) return null;
  const elapsed = formatElapsed(Date.now() - new Date(mob.markedAt).getTime());

  return (
    <Panel role="alert">
      <span style={{ fontSize: '1.1rem' }}>🆘 MAN OVERBOARD</span>
      {stats ? (
        <>
          <span>
            Distance <Big>{stats.dist < 10 ? stats.dist.toFixed(2) : stats.dist.toFixed(1)} nm</Big>
          </span>
          <span>
            Bearing <Big>{Math.round(stats.brg)}°</Big>
          </span>
        </>
      ) : (
        <span>Waiting for GPS…</span>
      )}
      <span>Elapsed {elapsed}</span>
      <ClearBtn onClick={onClear}>Clear MOB</ClearBtn>
    </Panel>
  );
}
